import React, { useEffect, useState } from "react";
import "./ManageWorkshops.css";
import { db } from "../../components/firebase/firebase";
import { Button, Input, Notification, Text, Title } from "../../components/ui";
import { formatDate } from "../../components/function";
import { Container, Flex, SafeArea } from "../../components/layout";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { TbEdit } from "react-icons/tb";
import { logEvent } from "../../utils/logger";

const MAX_DATA = 30; // Nombre max d'articles à charger

function ManageWorkshops() {
  const [data, setData] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async (reset = false) => {
    setLoading(true);

    try {
      let dataQuery = query(
        collection(db, "workshops"), // ✅ Utilisation correcte de collection()
        orderBy("createdAt", "desc"), // ✅ Tri par date descendante
        limit(MAX_DATA), // ✅ Limite du nombre d'articles
      );

      if (!reset && lastDoc) {
        dataQuery = query(dataQuery, startAfter(lastDoc)); // ✅ Pagination
      }

      const querySnapshot = await getDocs(dataQuery); // ✅ Récupération des documents
      const newArticles = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log(querySnapshot);
      setData((prev) => (reset ? newArticles : [...prev, ...newArticles]));
      setLastDoc(
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null,
      );
    } catch (error) {
      logEvent("ERROR", "DB", "Failed to fetch workshops", {
        error: error.message,
        stack: error.stack,
        isReset: reset,
      });
      console.error("Erreur lors du chargement des articles :", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    if (value.trim().length < 3) {
      fetchArticles(true);
      return;
    }

    try {
      const querySnapshot = await db
        .collection("workshops")
        .where("title", ">=", value)
        .where("title", "<=", value + "\uf8ff")
        .get();

      setData(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      logEvent("ERROR", "DB", "Workshops search failed", {
        error: error.message,
        stack: error.stack,
        searchQuery: value,
      });
      console.error("Erreur de recherche :", error);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sel) => sel !== id) : [...prev, id],
    );
  };

  const handleAllSelect = () => {
    if (selected.length === data.length) {
      setSelected([]);
    } else {
      setSelected(data.map((d) => d.id));
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    if (selected.length === 0) return;
    try {
      await Promise.all(
        selected.map((id) => deleteDoc(doc(db, "workshops", id))),
      );
      setData((prev) =>
        prev.filter((article) => !selected.includes(article.id)),
      );
      setSelected([]);
      setNotification("success");
      logEvent("INFO", "DB", "Workshops deleted", {
        deletedIds: selected,
        count: selected.length,
      });
    } catch (error) {
      logEvent("ERROR", "DB", "Workshops deletion failed", {
        error: error.message,
        stack: error.stack,
        attemptedIds: selected,
        count: selected.length,
      });
      console.error("Erreur lors de la suppression :", error);
      setNotification("error");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <SafeArea className="ManageWorkshops">
      {notification && (
        <Notification
          state={notification}
          msg={
            notification === "success"
              ? "Opération réussie !"
              : "Erreur lors de l'opération"
          }
          onClose={() => setNotification(null)}
        />
      )}
      <Flex type="col" gap={50}>
        <Title level="h3">Gérer les ateliers</Title>

        <Flex type="col" className="wrapper" gap={50}>
          <Input
            type="search"
            placeholder="Rechercher un atelier"
            onChange={handleSearch}
          />

          <Container className="result">
            <Flex type="between-center">
              <Text>{data.length} ateliers</Text>
              <Button
                skin="red"
                onClick={handleDelete}
                disabled={selected.length === 0}
                loading={deleteLoading}
              >
                Supprimer
              </Button>
            </Flex>

            <Flex type="col" gap={20}>
              <Container className="table">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <div
                          className={
                            "ctn-input" +
                            (selected.length !== 0 &&
                            selected.length === data.length
                              ? " active"
                              : "")
                          }
                          onClick={handleAllSelect}
                        >
                          <div className="check"></div>
                        </div>
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                        }}
                      >
                        Titre
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          paddingRight: "35px",
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          paddingRight: "45px",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((d, index) => (
                      <tr
                        key={index}
                        onClick={() => handleSelect(d.id)}
                        className={selected.includes(d.id) ? "active" : ""}
                      >
                        <td>
                          <div className="ctn-input">
                            <div className="check"></div>
                          </div>
                        </td>
                        <td>{d.title}</td>
                        <td style={{ textAlign: "right" }}>
                          {formatDate(d.createdAt)}
                        </td>
                        <td
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            text="Modifier"
                            skin="nobg"
                            iconLeft={<TbEdit />}
                            to={`/workshops/edit/${d.id}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Container>
              {lastDoc && (
                <Flex type="center">
                  <Button
                    text="Charger plus"
                    skin="nobg"
                    onClick={() => lastDoc && fetchArticles()}
                    loading={loading}
                  />
                </Flex>
              )}
            </Flex>
          </Container>
        </Flex>
      </Flex>
    </SafeArea>
  );
}

export default ManageWorkshops;
